<template>
  <div
    v-if="brandAssistantEnabled"
    class="brand-assistant-button"
    :class="{ 'brand-assistant-button--icon-only': iconOnly }"
  >
    <AkButton
      color="main-accent"
      size="md"
      symbol="stars"
      :only-icon="iconOnly"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      :aria-label="iconOnly ? $t('brand.assistant.askButton.label') : undefined"
      data-testid="brand-assistant-button"
      @click="toggle"
    >
      <!-- Dropped rather than hidden: an empty label still reserves its padding. -->
      <template v-if="!iconOnly">
        {{ $t('brand.assistant.askButton.label') }}
      </template>
    </AkButton>
  </div>
</template>

<script setup lang="ts">
import { AkButton } from '@ankorstore/design-system';
import { useAgent } from '../composables/use-agent/use-agent';
import { useBrandAssistant } from '../composables/use-agent/use-brand-assistant';

/**
 * Trigger only — the window is mounted once by BrandAssistantPanel.
 *
 * The plugin's own trigger stays collapsed; this button is the entry point.
 */
const { brandAssistantEnabled } = useAgent();
const { isOpen, toggle } = useBrandAssistant();

withDefaults(
  defineProps<{
    /**
     * Drops the label and keeps the glyph. Used by the mobile bar, which already
     * carries the burger and the logo — a labelled button would not fit beside
     * them.
     */
    iconOnly?: boolean;
  }>(),
  { iconOnly: false }
);
</script>

<style lang="scss" scoped>
.brand-assistant-button {
  display: flex;
  align-items: center;
  margin-right: 20px;

  /* The mobile bar spaces its own actions, so the desktop gutter would only
     push the button away from the edge it is meant to sit against. */
  &--icon-only {
    margin-right: 0;
  }
}
</style>
